import cn from "classnames";
import { useAtomValue } from "jotai";
import { type ReactNode, useLayoutEffect, useRef } from "react";
import { widgetMenuCoordinatesAtom } from "@/interface/AppStore";
import { VStack } from "@/shared/ui/Stack";
import type { FlexAlign } from "@/shared/ui/Stack/Flex/types";
import { calculateSubMenuCoordinates } from "../../lib";
import type { ActiveSubMenuState } from "../../types";
import styles from "./SubMenu.module.css";

interface SubMenuProps {
    activeSubMenu: ActiveSubMenuState;
    children: ReactNode;
    padding?: 0 | 4 | 8 | 12 | "aiAssistant";
    align?: FlexAlign;
    className?: string;
    alignToMenuLeft?: boolean;
}

const paddingStyles = {
    0: "",
    4: styles.padding4,
    8: styles.padding8,
    12: styles.padding12,
    aiAssistant: styles.paddingAiAssistant,
};

export function SubMenu({
    padding = 4,
    align,
    activeSubMenu,
    className,
    children,
    alignToMenuLeft = false,
}: SubMenuProps) {
    const coordinates = useAtomValue(widgetMenuCoordinatesAtom);
    const subMenuRef = useRef<HTMLDivElement>(null);

    const wrapperStyles = cn(styles.wrapper, paddingStyles[padding], className);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <Добавлена зависимость от coordinates для ререндера при перемещении по канвасу>
    useLayoutEffect(() => {
        if (!subMenuRef.current) {
            return;
        }

        const subMenu = subMenuRef.current;
        const anchorRect = activeSubMenu.anchor.getBoundingClientRect();
        const subMenuRect = subMenu.getBoundingClientRect();

        const { left: initialLeft, top } = calculateSubMenuCoordinates(anchorRect, subMenuRect);

        let left = initialLeft;

        if (alignToMenuLeft) {
            const menuRoot = activeSubMenu.anchor.closest("[data-widget-menu-root='true']");
            if (menuRoot instanceof HTMLElement) {
                left = menuRoot.getBoundingClientRect().left;
            }
        }

        subMenu.style.left = `${left}px`;
        subMenu.style.top = `${top}px`;
    }, [activeSubMenu.anchor, coordinates, alignToMenuLeft]);

    return (
        <VStack ref={subMenuRef} className={wrapperStyles} align={align} data-ui-root="true">
            {children}
        </VStack>
    );
}
